import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  AlertTriangle,
  Clock,
  Phone,
  Mail,
  ChevronRight,
  RefreshCw,
  UserX,
  Calendar,
  Bell,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const InactiveClientsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total_inactive: 0, clients: [] });

  useEffect(() => {
    fetchInactiveClients();
  }, []);

  const fetchInactiveClients = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/clients/inactive`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(response.data);
    } catch (error) {
      toast.error("Error al cargar clientes inactivos");
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-700 border-red-200";
      case "medium":
        return "bg-amber-100 text-amber-700 border-amber-200";
      default:
        return "bg-blue-100 text-blue-700 border-blue-200";
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "high":
        return "Urgente";
      case "medium":
        return "Moderado";
      default:
        return "Bajo";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Sin actividad";
    return new Date(dateStr).toLocaleDateString("es-DO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const highPriority = data.clients.filter((c) => c.priority === "high");
  const mediumPriority = data.clients.filter((c) => c.priority === "medium");

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800 flex items-center gap-2">
            <Bell className="text-amber-500" size={24} />
            Seguimiento de Clientes Inactivos
          </h2>
          <p className="text-sm text-slate-500">
            Clientes sin pedidos en los últimos 30 días
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchInactiveClients}
          data-testid="refresh-btn"
        >
          <RefreshCw size={16} className="mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-slate-100 rounded-full">
              <UserX size={20} className="text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{data.total_inactive}</p>
              <p className="text-sm text-slate-500">Total Inactivos</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded border border-red-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle size={20} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{highPriority.length}</p>
              <p className="text-sm text-slate-500">Prioridad Alta (+60 días)</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded border border-amber-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-amber-100 rounded-full">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{mediumPriority.length}</p>
              <p className="text-sm text-slate-500">Prioridad Media (30-60 días)</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alert Banner */}
      {highPriority.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="text-red-500 mt-0.5" size={20} />
          <div>
            <p className="font-semibold text-red-800">
              ¡Atención! {highPriority.length} cliente(s) requieren seguimiento urgente
            </p>
            <p className="text-sm text-red-600">
              Estos clientes no han realizado pedidos en más de 60 días
            </p>
          </div>
        </div>
      )}

      {/* Client List */}
      {data.clients.length === 0 ? (
        <div className="bg-white rounded border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserX size={32} className="text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-2">
            ¡Excelente! No hay clientes inactivos
          </h3>
          <p className="text-slate-500">
            Todos los clientes han realizado pedidos en los últimos 30 días
          </p>
        </div>
      ) : (
        <div className="bg-white rounded border border-slate-200 overflow-hidden">
          <div className="divide-y divide-slate-100">
            {data.clients.map((client) => (
              <div
                key={client.id}
                className="p-4 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/clients/${client.id}`)}
                data-testid={`inactive-client-${client.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-slate-800">{client.name}</h4>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(
                          client.priority
                        )}`}
                      >
                        {getPriorityLabel(client.priority)}
                      </span>
                      {client.was_visited_recently && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                          Visitado recientemente
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      {client.phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={14} />
                          {client.phone}
                        </span>
                      )}
                      {client.email && (
                        <span className="flex items-center gap-1">
                          <Mail size={14} />
                          {client.email}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar size={14} />
                        Última actividad: {formatDate(client.last_activity)}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 mt-2">
                      {client.days_inactive !== null && (
                        <span
                          className={`text-sm font-medium ${
                            client.days_inactive > 60
                              ? "text-red-600"
                              : client.days_inactive > 30
                              ? "text-amber-600"
                              : "text-blue-600"
                          }`}
                        >
                          {client.days_inactive} días sin actividad
                        </span>
                      )}
                      {client.balance > 0 && (
                        <span className="text-sm text-amber-600 font-medium">
                          Balance: RD$ {client.balance.toLocaleString("es-DO", { minimumFractionDigits: 2 })}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight size={20} className="text-slate-400" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default InactiveClientsPage;
